export type TokenResponse = { access_token: string; token_type: string; refresh_token?: string | null; expires_in?: number | null };
export type MeResponse = {
  id: string;
  email: string;
  is_admin: boolean;
  created_at: string;
  display_name?: string | null;
  preferred_language?: string | null;
  preferred_currency?: string | null;
  avatar_attachment_id?: string | null;
  avatar_url?: string | null;
};
export type InviteCreateResponse = { token: string; url?: string | null };
export type GroupResponse = {
  id: string;
  kind: string;
  name: string;
  description?: string | null;
  currency_code: string;
  created_at: string;
  avatar_attachment_id?: string | null;
  avatar_url?: string | null;
  member_count?: number | null;
};
export type FriendResponse = {
  friendship_id: string;
  friend_user_id: string;
  friend_email: string;
  friend_display_name?: string | null;
  friend_avatar_attachment_id?: string | null;
  friend_avatar_url?: string | null;
  group_id: string;
  created_at: string;
};
export type GroupMemberResponse = { user_id: string; email: string; display_name?: string | null; role: string; joined_at: string };
export type GroupMemberAddRequest = {
  user_id?: string | null;
  email?: string | null;
  display_name?: string | null;
};
export type ContactResponse = {
  id: string;
  contact_user_id?: string | null;
  email?: string | null;
  phone?: string | null;
  display_name?: string | null;
  local_display_name?: string | null;
  resolved_display_name: string;
  group_id?: string | null;
  avatar_attachment_id?: string | null;
  avatar_url?: string | null;
  contact_is_placeholder?: boolean | null;
};
export type MoneyUserAmount = { user_id: string; amount_minor: number };
export type BalanceItem = { user_id: string; net_minor: number };
export type SimplifiedTransfer = { from_user_id: string; to_user_id: string; amount_minor: number };
export type GroupBalancesResponse = { group_id: string; currency_code: string; balances: BalanceItem[]; simplified: SimplifiedTransfer[] };

export type ExpenseResponse = {
  id: string;
  group_id: string;
  description: string;
  amount_minor: number;
  currency_code: string;
  split_type: string;
  category_id?: string | null;
  note?: string | null;
  occurred_at: string;
  created_at: string;
  created_by_user_id: string;
  updated_at: string;
  updated_by_user_id?: string | null;
  is_deleted: boolean;
  payers: MoneyUserAmount[];
  splits: MoneyUserAmount[];
};

export type ExpenseCommentResponse = {
  id: string;
  user_id: string;
  body: string;
  created_at: string;
  edited_at?: string | null;
  edited_by_user_id?: string | null;
  is_deleted: boolean;
};

export type ExpenseAttachmentResponse = {
  id: string;
  attachment_id: string;
  original_filename?: string | null;
  content_type?: string | null;
  size_bytes?: number | null;
  created_at: string;
  download_url: string;
};
export type UploadAttachmentResponse = {
  id: string;
  download_url?: string | null;
};

export type ExpenseDetailResponse = ExpenseResponse & { comments: ExpenseCommentResponse[]; attachments: ExpenseAttachmentResponse[] };

export type SettlementResponse = {
  id: string;
  group_id: string;
  from_user_id: string;
  to_user_id: string;
  amount_minor: number;
  currency_code: string;
  note?: string | null;
  created_at: string;
  created_by_user_id: string;
  updated_at: string;
  updated_by_user_id?: string | null;
  is_deleted: boolean;
};

export type SettlementAttachmentResponse = {
  id: string;
  attachment_id: string;
  original_filename?: string | null;
  content_type?: string | null;
  size_bytes?: number | null;
  created_at: string;
  download_url: string;
};

export type CategoryResponse = { id: string; name: string; created_at: string };
export type FeaturesResponse = { debt_simplification: boolean; comments: boolean; attachments: boolean };

const TOKEN_KEY = "swk_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string) {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY);
}

async function apiFetch(path: string, init: RequestInit = {}) {
  const headers = new Headers(init.headers);
  headers.set("Content-Type", "application/json");
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);

  const res = await fetch(`/api${path}`, { ...init, headers });
  if (!res.ok) {
    const text = await res.text();
    if (res.status === 401) {
      clearToken();
      throw new Error("Unauthorized - please log in again");
    }
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res;
}

async function apiFetchAuthed(path: string, init: RequestInit = {}) {
  const headers = new Headers(init.headers);
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  const res = await fetch(`/api${path}`, { ...init, headers });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res;
}

export async function register(email: string, password: string, invite_token: string): Promise<TokenResponse> {
  const res = await apiFetch("/auth/register", {
    method: "POST",
    body: JSON.stringify({ email, password, invite_token }),
  });
  return res.json();
}

export async function login(email: string, password: string): Promise<TokenResponse> {
  const res = await apiFetch("/auth/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
  return res.json();
}

export async function me(): Promise<MeResponse> {
  const res = await apiFetch("/me", { method: "GET" });
  return res.json();
}

export async function updateProfile(payload: {
  display_name?: string | null;
  preferred_language?: string | null;
  preferred_currency?: string | null;
}): Promise<MeResponse> {
  const res = await apiFetch("/me", { method: "PUT", body: JSON.stringify(payload) });
  return res.json();
}

export async function setAvatar(attachment_id: string | null): Promise<MeResponse> {
  const res = await apiFetch("/me/avatar", { method: "POST", body: JSON.stringify({ attachment_id }) });
  return res.json();
}

export async function changePassword(current_password: string, new_password: string): Promise<void> {
  await apiFetch("/auth/change-password", {
    method: "POST",
    body: JSON.stringify({ current_password, new_password }),
  });
}

export async function uploadFile(file: File): Promise<UploadAttachmentResponse> {
  const fd = new FormData();
  fd.append("upload", file);
  const token = getToken();
  const res = await fetch(`/api/files/upload`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : undefined,
    body: fd,
  });
  if (!res.ok) {
    const text = await res.text();
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.json();
}

export async function getFeatures(): Promise<FeaturesResponse> {
  const res = await apiFetch("/features", { method: "GET" });
  return res.json();
}

export async function createInvite(): Promise<InviteCreateResponse> {
  const res = await apiFetch("/invites", { method: "POST", body: JSON.stringify({}) });
  return res.json();
}

export async function listGroups(): Promise<GroupResponse[]> {
  const res = await apiFetch("/groups", { method: "GET" });
  return res.json();
}

export async function createGroup(payload: {
  name: string;
  currency_code: string;
  description?: string | null;
  avatar_attachment_id?: string | null;
  member_ids?: string[];
}): Promise<GroupResponse> {
  const res = await apiFetch("/groups", { method: "POST", body: JSON.stringify(payload) });
  return res.json();
}

export async function joinGroup(token: string): Promise<GroupResponse> {
  const res = await apiFetch("/groups/join", { method: "POST", body: JSON.stringify({ token }) });
  return res.json();
}

export async function updateGroup(
  groupId: string,
  payload: { name?: string | null; description?: string | null; avatar_attachment_id?: string | null },
): Promise<GroupResponse> {
  const res = await apiFetch(`/groups/${groupId}`, { method: "PATCH", body: JSON.stringify(payload) });
  return res.json();
}

export async function listFriends(): Promise<FriendResponse[]> {
  const res = await apiFetch("/friends", { method: "GET" });
  return res.json();
}

export async function listContacts(): Promise<ContactResponse[]> {
  const res = await apiFetch("/contacts", { method: "GET" });
  return res.json();
}

export async function addFriend(email: string): Promise<FriendResponse> {
  const res = await apiFetch("/friends", { method: "POST", body: JSON.stringify({ email }) });
  return res.json();
}

export async function createContact(payload: {
  name?: string | null;
  email?: string | null;
  phone?: string | null;
  local_display_name?: string | null;
}): Promise<ContactResponse> {
  const res = await apiFetch("/contacts", { method: "POST", body: JSON.stringify(payload) });
  return res.json();
}

export async function updateContact(contactId: string, payload: { email?: string | null; phone?: string | null; local_display_name?: string | null }) {
  const res = await apiFetch(`/contacts/${contactId}`, { method: "PATCH", body: JSON.stringify(payload) });
  return res.json() as Promise<ContactResponse>;
}

export async function deleteContact(contactId: string): Promise<void> {
  await apiFetch(`/contacts/${contactId}`, { method: "DELETE" });
}

export async function getGroup(groupId: string): Promise<GroupResponse> {
  const res = await apiFetch(`/groups/${groupId}`, { method: "GET" });
  return res.json();
}

export async function createGroupInvite(groupId: string): Promise<{ token: string; url?: string | null }> {
  const res = await apiFetch(`/groups/${groupId}/invites`, { method: "POST", body: JSON.stringify({}) });
  return res.json();
}

export async function leaveGroup(groupId: string): Promise<void> {
  await apiFetch(`/groups/${groupId}/leave`, { method: "POST", body: JSON.stringify({}) });
}

export type GroupInviteResponse = {
  token: string;
  created_at: string;
  created_by_user_id: string;
  used_by_user_id?: string | null;
  used_at?: string | null;
  expires_at?: string | null;
  revoked_at?: string | null;
  url?: string | null;
};

export async function listGroupInvites(groupId: string): Promise<GroupInviteResponse[]> {
  const res = await apiFetch(`/groups/${groupId}/invites`, { method: "GET" });
  return res.json();
}

export async function revokeGroupInvite(groupId: string, token: string): Promise<void> {
  await apiFetch(`/groups/${groupId}/invites/${encodeURIComponent(token)}/revoke`, { method: "POST", body: JSON.stringify({}) });
}

export async function removeGroupMember(groupId: string, memberUserId: string): Promise<void> {
  await apiFetch(`/groups/${groupId}/members/${memberUserId}`, { method: "DELETE" });
}

export async function listGroupMembers(groupId: string): Promise<GroupMemberResponse[]> {
  const res = await apiFetch(`/groups/${groupId}/members`, { method: "GET" });
  return res.json();
}

export async function addGroupMember(groupId: string, payload: GroupMemberAddRequest): Promise<GroupMemberResponse> {
  const res = await apiFetch(`/groups/${groupId}/members`, { method: "POST", body: JSON.stringify(payload) });
  return res.json();
}

export async function getGroupBalances(groupId: string): Promise<GroupBalancesResponse> {
  const res = await apiFetch(`/groups/${groupId}/balances`, { method: "GET" });
  return res.json();
}

export async function listGroupExpenses(groupId: string, includeDeleted = false): Promise<ExpenseResponse[]> {
  const res = await apiFetch(`/groups/${groupId}/expenses?include_deleted=${includeDeleted ? "true" : "false"}`, { method: "GET" });
  return res.json();
}

export async function getGroupExpense(groupId: string, expenseId: string): Promise<ExpenseDetailResponse> {
  const res = await apiFetch(`/groups/${groupId}/expenses/${expenseId}`, { method: "GET" });
  return res.json();
}

export async function createGroupExpense(groupId: string, payload: any): Promise<ExpenseResponse> {
  const res = await apiFetch(`/groups/${groupId}/expenses`, { method: "POST", body: JSON.stringify(payload) });
  return res.json();
}

export async function updateGroupExpense(groupId: string, expenseId: string, payload: any): Promise<ExpenseResponse> {
  const res = await apiFetch(`/groups/${groupId}/expenses/${expenseId}`, { method: "PUT", body: JSON.stringify(payload) });
  return res.json();
}

export async function deleteGroupExpense(groupId: string, expenseId: string): Promise<void> {
  await apiFetch(`/groups/${groupId}/expenses/${expenseId}`, { method: "DELETE" });
}

export async function restoreGroupExpense(groupId: string, expenseId: string): Promise<void> {
  await apiFetch(`/groups/${groupId}/expenses/${expenseId}/restore`, { method: "POST", body: JSON.stringify({}) });
}

export async function listGroupSettlements(groupId: string, includeDeleted = false): Promise<SettlementResponse[]> {
  const res = await apiFetch(`/groups/${groupId}/settlements?include_deleted=${includeDeleted ? "true" : "false"}`, { method: "GET" });
  return res.json();
}

export async function createGroupSettlement(groupId: string, payload: any): Promise<SettlementResponse> {
  const res = await apiFetch(`/groups/${groupId}/settlements`, { method: "POST", body: JSON.stringify(payload) });
  return res.json();
}

export async function updateGroupSettlement(groupId: string, settlementId: string, payload: any): Promise<SettlementResponse> {
  const res = await apiFetch(`/groups/${groupId}/settlements/${settlementId}`, { method: "PUT", body: JSON.stringify(payload) });
  return res.json();
}

export async function deleteGroupSettlement(groupId: string, settlementId: string): Promise<void> {
  await apiFetch(`/groups/${groupId}/settlements/${settlementId}`, { method: "DELETE" });
}

export async function restoreGroupSettlement(groupId: string, settlementId: string): Promise<void> {
  await apiFetch(`/groups/${groupId}/settlements/${settlementId}/restore`, { method: "POST", body: JSON.stringify({}) });
}

export async function listCategories(): Promise<CategoryResponse[]> {
  const res = await apiFetch("/categories", { method: "GET" });
  return res.json();
}

export async function createCategory(name: string): Promise<CategoryResponse> {
  const res = await apiFetch("/categories", { method: "POST", body: JSON.stringify({ name }) });
  return res.json();
}

export async function createExpenseComment(groupId: string, expenseId: string, body: string): Promise<ExpenseCommentResponse> {
  const res = await apiFetch(`/groups/${groupId}/expenses/${expenseId}/comments`, { method: "POST", body: JSON.stringify({ body }) });
  return res.json();
}

export async function updateExpenseComment(groupId: string, expenseId: string, commentId: string, body: string): Promise<ExpenseCommentResponse> {
  const res = await apiFetch(`/groups/${groupId}/expenses/${expenseId}/comments/${commentId}`, { method: "PUT", body: JSON.stringify({ body }) });
  return res.json();
}

export async function deleteExpenseComment(groupId: string, expenseId: string, commentId: string): Promise<void> {
  await apiFetch(`/groups/${groupId}/expenses/${expenseId}/comments/${commentId}`, { method: "DELETE" });
}

export async function restoreExpenseComment(groupId: string, expenseId: string, commentId: string): Promise<void> {
  await apiFetch(`/groups/${groupId}/expenses/${expenseId}/comments/${commentId}/restore`, { method: "POST", body: JSON.stringify({}) });
}

export async function listExpenseAttachments(groupId: string, expenseId: string): Promise<ExpenseAttachmentResponse[]> {
  const res = await apiFetch(`/groups/${groupId}/expenses/${expenseId}/attachments`, { method: "GET" });
  return res.json();
}

export async function uploadExpenseAttachment(groupId: string, expenseId: string, file: File): Promise<ExpenseAttachmentResponse> {
  const fd = new FormData();
  fd.append("upload", file);
  const res = await apiFetchAuthed(`/groups/${groupId}/expenses/${expenseId}/attachments`, { method: "POST", body: fd });
  return res.json();
}

export async function listSettlementAttachments(groupId: string, settlementId: string): Promise<SettlementAttachmentResponse[]> {
  const res = await apiFetch(`/groups/${groupId}/settlements/${settlementId}/attachments`, { method: "GET" });
  return res.json();
}

export async function uploadSettlementAttachment(
  groupId: string,
  settlementId: string,
  file: File,
): Promise<SettlementAttachmentResponse> {
  const fd = new FormData();
  fd.append("upload", file);
  const res = await apiFetchAuthed(`/groups/${groupId}/settlements/${settlementId}/attachments`, { method: "POST", body: fd });
  return res.json();
}

export async function fetchAuthedBlob(url: string): Promise<Blob> {
  const token = getToken();
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : undefined });
  if (!res.ok) {
    const text = await res.text();
    if (res.status === 401) {
      clearToken();
      throw new Error("Unauthorized - please log in again");
    }
    throw new Error(text || `HTTP ${res.status}`);
  }
  return res.blob();
}

export async function openAuthedFile(url: string, filename?: string): Promise<void> {
  const blob = await fetchAuthedBlob(url);
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl;
  a.target = "_blank";
  a.rel = "noreferrer";
  if (filename) a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Keep it alive a bit for the new tab to load.
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}
